import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BreadcrumbSchema from "@/components/schemas/BreadcrumbSchema";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowRight, Sparkles } from "lucide-react";

const aboutPreviewImages = [
  {
    src: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/Industry-images/Manufacturing-robots.jpg",
    alt: "Robotics operating in a manufacturing environment",
  },
  {
    src: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/Industry-images/Warehouse_2.jpg",
    alt: "Industrial warehouse operations",
  },
  {
    src: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/Industry-images/aerial-port_cointainers_2.jpg",
    alt: "Port and logistics infrastructure",
  },
];

const breadcrumbItems = [
  { name: "Home", url: "https://klexu.com" },
  { name: "About", url: "https://klexu.com/about" },
];

const pillars = [
  {
    title: "End-to-End Accountability",
    desc: "We stay responsible from the first operational assessment through deployment, measurement, and ongoing performance review.",
  },
  {
    title: "Clarity in Complexity",
    desc: "We translate interconnected operational, commercial, and financial challenges into decisions teams can understand and execute.",
  },
  {
    title: "Evidence-Led Delivery",
    desc: "Every recommendation is grounded in verifiable measures, transparent assumptions, and outcomes that can be tracked over time.",
  },
  {
    title: "Integrated Expertise",
    desc: "We connect industrial operations, technology, commercial design, and finance to move transformation from plan to performance.",
  },
];

const About = () => {
  useDocumentTitle(
    "About Klexu | The Performance Engine for Robotics",
    "Klexu is the performance transformation engine for robotics — helping OEMs and operators shift from CapEx sales to agile, performance-based machine models."
  );

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: "https://klexu.com/about",
    name: "About Klexu — The Performance Engine for Robotics",
    about: { "@id": "https://klexu.com/#klexu" },
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <link rel="canonical" href="https://klexu.com/about" />
        <meta property="og:url" content="https://klexu.com/about" />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>
      <BreadcrumbSchema items={breadcrumbItems} />
      <Navbar />

      <main className="pb-20">
        <section className="relative overflow-hidden border-t border-border/40 bg-background py-20 lg:py-24">
          <div className="absolute inset-0 bg-hero-glow opacity-70" />
          <div className="absolute inset-0 bg-grid-pattern bg-[length:30px_30px] opacity-[0.3]" />

          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="font-medium">Industrial Impact Advisory</span>
                </div>

                <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                  Operator-Led. <span className="text-primary">Outcome-Driven.</span>
                </h1>

                <p className="max-w-xl text-lg leading-relaxed text-muted-foreground">
                  We help industrial leaders tackle complex transformation across operations,
                  technology, and growth — where execution matters more than theory. Klexu brings
                  operator-grade experience to align teams, accelerate momentum, and deliver
                  measurable results.
                </p>

                <div className="flex flex-wrap gap-4 pt-2">
                  <Button asChild size="lg">
                    <Link to="/consultation">
                      Book a Consultation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="#how-we-work">How We Work</a>
                  </Button>
                </div>
              </div>

              <div className="hidden items-center justify-center lg:flex">
                <div className="w-full max-w-md overflow-hidden rounded-xl border border-border/50 bg-card shadow-2xl">
                  <div className="flex items-center justify-between bg-primary px-4 py-3 text-primary-foreground">
                    <div className="flex items-center gap-2" aria-hidden="true">
                      <div className="h-3 w-3 rounded-full bg-destructive/70" />
                      <div className="h-3 w-3 rounded-full bg-accent/80" />
                      <div className="h-3 w-3 rounded-full bg-primary-foreground/70" />
                    </div>
                    <span className="text-sm font-medium">Industrial Impact</span>
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="grid h-[300px] grid-cols-2 grid-rows-2 gap-3 bg-background p-6">
                    <img
                      src={aboutPreviewImages[0].src}
                      alt={aboutPreviewImages[0].alt}
                      className="col-span-2 h-full w-full rounded-md object-cover"
                    />
                    {aboutPreviewImages.slice(1).map((image) => (
                      <img
                        key={image.src}
                        src={image.src}
                        alt={image.alt}
                        className="h-full w-full rounded-md object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto max-w-6xl px-4 pt-16 sm:px-6 lg:px-8">
          <div className="mb-16 grid gap-6 md:grid-cols-2">
            <div className="glass-card rounded-xl p-8">
              <h2 className="mb-4 text-2xl font-bold">Our Mission</h2>
              <p className="leading-relaxed text-muted-foreground">
                Move industrial businesses from rigid machine ownership to agile,
                performance-based models that create lasting value.
              </p>
            </div>
            <div className="glass-card rounded-xl p-8">
              <h2 className="mb-4 text-2xl font-bold">Our Vision</h2>
              <p className="leading-relaxed text-muted-foreground">
                Build the transformation engine for how industrial assets are contracted,
                financed, deployed, and governed.
              </p>
            </div>
          </div>

          <div id="how-we-work" className="mb-16 scroll-mt-24">
            <h2 className="text-2xl font-bold mb-2 text-center">How We Work</h2>
            <p className="text-center text-muted-foreground mb-8">
              The principles behind practical, measurable industrial transformation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pillars.map((pillar) => (
                <div key={pillar.title} className="p-6 bg-white/5 border border-white/10 rounded-lg">
                  <h3 className="text-xl font-medium mb-3">{pillar.title}</h3>
                  <p className="text-muted-foreground">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="glass-card p-10 rounded-xl text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">
              Ready to deploy robotics on performance terms?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Let's map your fleet, contracts, and capital structure onto the flywheel.
            </p>
            <Button asChild size="lg" className="group">
              <Link to="/consultation">
                Book a Consultation
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
